import { casesOf, type OrderItem, OrderItemSchema, unitsOf } from '../../../../data/order-item';
import type { OrderOptions } from '../../../../data/order-options';
import type { Product } from '../../../../data/product';
import { getSuggestedQuantities } from '../utils';

export type ProductOrderItem = {
  product: Product;
  suggested_cases?: number;
  cases?: number;
  suggested_units?: number;
  units?: number;
};

export function itemHasCount(orderItem: ProductOrderItem): boolean {
  return casesOf(orderItem) > 0 || unitsOf(orderItem) > 0;
}

export function itemHasOnlyUnits(orderItem: ProductOrderItem): boolean {
  return casesOf(orderItem) === 0 && unitsOf(orderItem) > 0;
}

export function itemHasUserCount(orderItem: ProductOrderItem): boolean {
  return orderItem.cases !== undefined || orderItem.units !== undefined;
}

export function replaceItem(orderItems: ProductOrderItem[], newItem: ProductOrderItem) {
  return orderItems.map((item) => (item.product.id === newItem.product.id ? newItem : item));
}

/**
 * Build the per-product working list from a (possibly sparse) server order.
 * Used on first load and when reorder options change — both need the full
 * `Product` from `products` because `OrderItem.product` is only a snapshot.
 */
export function initOrderItems(
  orderItems: OrderItem[],
  products: Product[],
  options: OrderOptions
): ProductOrderItem[] {
  const itemsByProductId = new Map(orderItems.map((item) => [item.product.id, item]));

  return products.map((product) => ({
    ...itemsByProductId.get(product.id),
    ...getSuggestedQuantities(product, options),
    product,
  }));
}

/**
 * Recompute suggested quantities and re-attach the latest `Product` reference
 * for each entry. Used after a refresh merge and whenever the React Query
 * products cache patches a field (e.g. `min_qoh`, `disabled`) — without the
 * re-attach, the row's reorder label keeps reading the snapshot captured at
 * mount and the table never re-sorts.
 */
export function updateOrderQuanties(
  orderItems: ProductOrderItem[],
  products: Product[],
  options: OrderOptions
): ProductOrderItem[] {
  const productsById = new Map(products.map((p) => [p.id, p]));

  return orderItems.map((item) => {
    const product = productsById.get(item.product.id) ?? item.product;

    return {
      ...item,
      ...getSuggestedQuantities(product, options),
      product,
    };
  });
}

export function simplifyOrderItem(orderItem: ProductOrderItem): OrderItem {
  return OrderItemSchema.cast(orderItem, { stripUnknown: true });
}

// add a new dirty item to the existing list, replacing any previous item for the same product
export function mergeDirtyItems(
  dirtyItems: ProductOrderItem[],
  newItem: ProductOrderItem
): ProductOrderItem[] {
  const newDirtyItems = dirtyItems.filter((item) => item.product.id !== newItem.product.id);

  return [...newDirtyItems, newItem];
}

export function mergeOrderItems({
  existingItems,
  incomingItems,
}: {
  existingItems: ProductOrderItem[];
  incomingItems: OrderItem[];
}): ProductOrderItem[] {
  const incomingItemsMap = new Map(incomingItems.map((item) => [item.product.id, item]));

  return existingItems.map((existingItem) => {
    const incomingItem = incomingItemsMap.get(existingItem.product.id);
    if (!incomingItem) {
      return existingItem;
    }

    return {
      ...existingItem,
      cases: incomingItem.cases,
      units: incomingItem.units,
    };
  });
}

type GetRemainingDirtyItemsProperties = {
  dirtyItems: ProductOrderItem[];
  updatedItems: OrderItem[];
};

// find locally dirty product order items that do not match the updated items. These items need to remain in the dirty list
// for future changes.
export function getRemainingDirtyItems({
  dirtyItems,
  updatedItems,
}: GetRemainingDirtyItemsProperties): ProductOrderItem[] {
  return dirtyItems.filter((dirtyItem) => {
    const updatedItem = updatedItems.find(
      (updatedItem) => updatedItem.product.id === dirtyItem.product.id
    );

    return (
      !updatedItem || dirtyItem.cases !== updatedItem.cases || dirtyItem.units !== updatedItem.units
    );
  });
}
